import ObjectId from 'bson-objectid';
import {
  Button,
  Calendar,
  ChangeAvatar,
  Col,
  Dropdown,
  Input,
  Row,
  Spinner2,
  Text
} from 'components';
import { useOutsideClick } from 'core';
import { format, subYears } from 'date-fns';
import { Api, nations, theme, validateEmail } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IrreversableModal } from '../../';
import { Games, Header, MySports } from './';

const initialSportsData = [
  {
    sport: '',
    favorite: '',
    position: ''
  }
];

const Team = ({ team, callback, closeModal, showRemoveAlert, setShowRemoveAlert, onSave }) => {
  const [name, setName] = useState(''),
    [username, setUsername] = useState(''),
    [email, setEmail] = useState(''),
    [bio, setBio] = useState(''),
    [password, setPassword] = useState(''),
    [league, setLeague] = useState(''),
    [avatar, setAvatar] = useState(null),
    [loading, setLoading] = useState(false),
    [errs, setErrs] = useState([]),
    [phone, setPhone] = useState(''),
    [phoneNation, setPhoneNation] = useState(nations[0]),
    [country, setCountry] = useState(''),
    [foundedDate, setFoundedDate] = useState(null),
    [showCalendar, setShowCalendar] = useState(false),
    [sports, setSports] = useState(initialSportsData),
    calendarRef = useRef(),
    dispatch = useDispatch();

  useOutsideClick(calendarRef, () => showCalendar && setShowCalendar(false), [
    'react-datepicker__year-option'
  ]);

  useEffect(() => {
    if (team) {
      setName(team?.name || '');
      setUsername(team?.username || '');
      setEmail(team?.email?.includes('@') ? team.email : '');
      setBio(team?.bio || '');
      setLeague(team.league || '');
      setPhone(team.phone || '');
      setPhoneNation(nations.find((x) => x.icon === team.phoneNation) || nations[0]);
      setCountry(nations.find((x) => x.value?.toLowerCase() === team?.country?.toLowerCase()));
      setFoundedDate(team?.birthday && new Date(team.birthday));
      setSports(team?.sports?.length ? team?.sports : initialSportsData);
    }
  }, []);

  async function submit() {
    setLoading(true);
    setErrs([]);
    const newErrs = [];
    if (!name) newErrs.push({ input: 'name', err: 'Required' });
    if (!username) newErrs.push({ input: 'username', err: 'Required' });
    if (email && !validateEmail(email)) errs.push({ input: 'email', err: 'Not a valid email' });
    if (!sports.length) newErrs.push({ input: 'sport', err: 'Required' });
    if (password && password.length < 8)
      newErrs.push({ input: 'password', err: 'At least 8 characters' });

    if (newErrs.length) {
      setErrs(newErrs);

      return setLoading(false);
    }

    let data = new FormData();

    data.append('name', name);
    data.append('username', username);
    data.append('email', email);
    data.append('league', league);
    data.append('bio', bio);
    data.append('type', 'team');
    country && data.append('country', country?.value);
    data.append('sports', JSON.stringify(sports));
    if (phone) {
      data.append('phone', phone);
      data.append('phoneNation', phoneNation.icon);
    }
    team && data.append('userId', team._id);
    password && data.append('password', password);
    foundedDate && data.append('birthday', foundedDate.toISOString());

    if (avatar) {
      const avatarId = ObjectId().toString();
      data.append('avatar', avatar, `${avatarId}-avatar.png`);
      data.append('fileName', `${avatarId}-avatar.png`);
    }

    const res = await Api.post(team ? '/admin/editUser' : '/admin/createUser', data, 'createUser');

    if (!res) return setLoading(false);

    setTimeout(() => {
      setName('');
      setUsername('');
      setEmail('');
      setPassword('');
      setLeague('');
      setAvatar('');
      setFoundedDate(null);
      setCountry('');
      setSports(initialSportsData);

      dispatch({
        type: 'HANDLE_RES',
        payload: { res: team ? 'Team updated' : 'Team created' }
      });

      dispatch({
        type: team ? 'ADMIN_EDIT_TEAM' : 'ADMIN_ADD_TEAM',
        payload: res
      });

      callback && callback(res);
      onSave && onSave();
      closeModal && closeModal();
      setLoading(false);
    }, 1000);
  }

  async function removeTeam() {
    setLoading(true);

    const res = await Api.post('/users/remove', { userId: team._id });

    dispatch({
      type: 'ADMIN_REMOVE_DATA',
      payload: team._id
    });

    closeModal();
    setLoading(false);
  }

  return (
    <Col pad="24px" pos="relative">
      <Spinner2 fullScreen show={loading} />

      <Row start>
        <Col noFlex marg="0 24px 0 0" style={{ zIndex: 1, position: 'relative' }}>
          <ChangeAvatar user={team} onFileSelect={(image) => setAvatar(image)} />
        </Col>

        <Header team={team} />
      </Row>

      <Text h4 bold marg="40px 0 24px">
        Team info
      </Text>

      <Input
        title="Team name"
        placeholder="Team name..."
        onChange={({ target }) => setName(target.value)}
        value={name}
        error={errs.find((x) => x.input === 'name')?.err}
      />

      <Input
        marg="24px 0 0"
        placeholder="Username..."
        title="Username"
        onChange={({ target }) => setUsername(target.value)}
        value={username}
        error={errs.find((x) => x.input === 'username')?.err}
      />

      <Input
        marg="24px 0 0"
        title="Email"
        placeholder="Email..."
        onChange={({ target }) => setEmail(target.value)}
        value={email}
        error={errs.find((x) => x.input === 'email')?.err}
      />

      <Input
        marg="24px 0 0"
        title="Password"
        password
        placeholder="***********"
        onChange={({ target }) => setPassword(target.value)}
        error={errs.find((x) => x.input === 'password')?.err}
        value={password}
      />

      <Input
        marg="24px 0 0"
        title="League or division"
        placeholder="League or division..."
        value={league}
        onChange={({ target }) => setLeague(target.value)}
      />

      <Input
        title="Bio"
        marg="24px 0 0"
        multiline
        placeholder="Team bio..."
        ht="100px"
        value={bio}
        onChange={({ target }) => setBio(target.value)}
      />

      <Text h4 bold marg="40px 0 24px">
        Additional info
      </Text>

      <Dropdown
        title="Founded date"
        admin
        handleSelect={(e) => {}}
        onClick={() => setShowCalendar(!showCalendar)}
        selected={foundedDate && format(new Date(foundedDate), 'dd MMMM, yyyy')}
        placeholder="Founded date..."
        bg="#fff"
        rounded
        wid="100%"
      />

      {showCalendar && (
        <Row start reverse wid="100%" ht="0">
          <Col
            pos="relative"
            pad="8px"
            bg="#fff"
            shadow
            hasRadius="16px"
            top="0"
            noFlex
            ref={calendarRef}
          >
            <Calendar
              showYearDropdown
              yearDropdownItemNumber={100}
              minDate={subYears(new Date(), 100)}
              selected={new Date(foundedDate || new Date())}
              maxDate={new Date()}
              onChange={(date) => {
                setFoundedDate(date);
                setShowCalendar(false);
              }}
            />
          </Col>
        </Row>
      )}

      <Dropdown
        title="Country"
        marg="24px 0 0"
        admin
        options={nations}
        selected={country}
        handleSelect={(e) => setCountry(e)}
        placeholder="Country..."
        bg="#fff"
        rounded
        wid="100%"
      />

      <Input
        title="Phone number"
        marg="24px 0 0"
        placeholder="Phone number..."
        onChange={({ target }) => setPhone(target.value)}
        phone
        nation={phoneNation}
        handleNationSelect={setPhoneNation}
        value={phone}
        error={errs.find((x) => x.input === 'phone')?.err}
      />

      <MySports data={sports} setData={setSports} type="Team" />

      <Row marg="56px 0 0">
        <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

        <Button
          big
          onClick={submit}
          text={team ? 'Update team' : 'Create team'}
          bg={theme.SECONDARY}
          action="createUser"
        />
      </Row>

      <IrreversableModal
        visible={showRemoveAlert}
        onConfirm={removeTeam}
        closeModal={() => setShowRemoveAlert(false)}
        text="Be cautious, this change will also effect any of the content related to this team"
        heading={`Are you sure you want to delete this team?`}
      />
    </Col>
  );
};

export default Team;
